import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Card, Form, InputGroup, Modal, Row, Spinner, Toast, ToastContainer } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { commonStrings } from "../../../../../globalization";
import { AdmPanelContainer } from "../../../../adm-panel/site/api/screens";
import { ActionType, DataResponse, IProduct } from "../../../business";
import { ProductControllerImpl } from "../../../business/impl";
import { ProductListFactoryImpl } from "../../../site/impl/factories";

export function ProductListPage() {
    const { t } = useTranslation('product');
    const cs = useMemo(() => commonStrings().PTBR.dashboard.auto_part.list, []);
    const initialProductValues = useMemo(() => ({
        id: '',
        image: '',
        is_new: false,
        part_name: '',
        price: 0,
        quantity: 1,
        url: ''
    }), []);

    const [productsList, setProductsList] = useState<DataResponse>();
    const [product, setProduct] = useState<IProduct>(initialProductValues);
    const [productStock, setProductStock] = useState(1);
    const [isLoading, setLoading] = useState(true);
    const [isProcessing, setProcessing] = useState(false);
    const [showModal, setShowModal] = useState<{ type: ActionType | null, open: boolean }>({ type: null, open: false });
    const [toast, setToast] = useState<{ message: string, type: string }>({ message: '', type: 'success' });

    const controller = useMemo(() => new ProductControllerImpl(), []);
    const factory = useMemo(() => new ProductListFactoryImpl(), []);

    useEffect(() => {
        (async () => {
            try {
                const response = await controller.productRequest("products-adm/list");
                setProductsList(response);
            } catch (error) {
                console.error("Erro na requisição:", error);
                setProductsList({ success: false, response: [] });
            } finally {
                setLoading(false);
            }
        })();
    }, [controller]);

    const handleShowModal = useCallback((product: IProduct, action: ActionType) => {
        setProduct(product);
        setProductStock(product.quantity);
        setShowModal({ type: action, open: true });
    }, []);

    const handleCloseModal = useCallback(() => {
        setShowModal({ type: null, open: false });
    }, []);

    const updateProductQuantity = useCallback((productId: string, newQuantity: number) => {
        setProductsList((prev) => {
            if (!prev?.response) return prev;
            return {
                ...prev,
                response: prev.response.map((prod) =>
                    prod.id === productId ? { ...prod, quantity: newQuantity } : prod
                )
            };
        });
    }, []);

    const handleDeleteProduct = useCallback(async () => {
        setProcessing(true);
        const response = await controller.onProductDelete(product.id);
        if (response.success) {
            setProductsList((prev) => ({ ...prev, response: prev?.response?.filter(p => p.id !== product.id) || [] }));
            setToast({ message: t('deletedFeedback'), type: 'success' });
        }
        setProcessing(false);
        handleCloseModal();
    }, [controller, product.id, t, handleCloseModal]);

    const handleStockUpdate = useCallback(async (event) => {
        event.preventDefault();
        setLoading(true);
        try {
            const response = await controller.stockUpdateRegister(product.id, productStock);
            if (response.success) {
                setToast({ message: t('stockUpdated'), type: 'success' });
                updateProductQuantity(product.id, productStock);
            } else {
                if (response.description)
                    setToast({ message: response.description[0] || 'Erro', type: 'danger' });
            }
        } catch (error) {
            setToast({ message: error.toString(), type: 'danger' });
        } finally {
            setLoading(false);
            handleCloseModal();
        }
    }, [controller, product.id, productStock, t, updateProductQuantity, handleCloseModal]);

    return (
        <AdmPanelContainer>
            <h4 className="mb-4">{cs.title}</h4>
            <Card>
                <Card.Body>
                    <Row>
                        {isLoading ? <p>{t('loading')}</p> : factory.create(productsList, handleShowModal, t)}
                    </Row>
                </Card.Body>
            </Card>
            <Modal show={showModal.open} onHide={handleCloseModal} backdrop="static" keyboard={false}>
                <Modal.Header closeButton>
                    <Modal.Title>{showModal.type === ActionType.DELETE ? t('delete') : t('stockTitle')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {showModal.type === ActionType.DELETE ? (
                        <>
                            {t('deleteDisclaimer')}
                            <p dangerouslySetInnerHTML={{ __html: t('deleteQuestion', { productName: product.part_name }) }} />
                        </>
                    ) : (
                        <>
                            <p dangerouslySetInnerHTML={{ __html: t('stockSubtitle', { productName: product.part_name }) }} />
                            <Form noValidate onSubmit={handleStockUpdate}>
                                <InputGroup>
                                    <Button onClick={() => setProductStock((prev) => Math.max(prev - 1, 0))}>-</Button>
                                    <Form.Control
                                        type="number"
                                        value={productStock}
                                        onChange={(e) => setProductStock(Number(e.target.value))}
                                        min={0}
                                        step={1}
                                        className="text-center"
                                    />
                                    <Button onClick={() => setProductStock((prev) => prev + 1)}>+</Button>
                                </InputGroup>
                                <div className="d-grid gap-2 mt-5">
                                    <Button type="submit" variant="primary" size="lg" disabled={false}>
                                        {isLoading ? <Spinner /> : t('continue')}
                                    </Button>
                                </div>
                            </Form>
                        </>
                    )}
                </Modal.Body>
                {showModal.type === ActionType.DELETE && (
                    <Modal.Footer className="justify-content-between">
                        <Button variant="secondary" onClick={handleCloseModal}>{t('deleteCancelButton')}</Button>
                        <Button variant="danger" onClick={handleDeleteProduct} disabled={isProcessing}>{t('deleteConfirmButton')}</Button>
                    </Modal.Footer>
                )}
            </Modal>

            <ToastContainer
                className="p-3"
                position={'top-end'}
                style={{ zIndex: 1 }}
            >
                <Toast onClose={() => setToast({ message: '', type: 'success' })} show={toast.message !== ''} delay={7000} bg={toast.type} autohide>
                    <Toast.Header>
                        <strong className="me-auto">Montacar</strong>
                    </Toast.Header>
                    <Toast.Body className={'text-white'}>{toast.message}</Toast.Body>
                </Toast>
            </ToastContainer>
        </AdmPanelContainer>
    );
}
